import { JavascriptLogoIcon } from 'src/icons/javascript-logo-icon';
import { NextJsLogoIcon } from 'src/icons/nextjs-logo-icon';
import { NodeJsLogoIcon } from 'src/icons/nodejs-logo-icon';
import { PythonLogoIcon } from 'src/icons/python-logo-icon';
import { ReactLogoIcon } from 'src/icons/react-logo-icon';
import { RemixLogoIcon } from 'src/icons/remix-logo-icon';
import { RubyLogoIcon } from 'src/icons/ruby-logo-icon';

export const FRONTEND_STACK = {
  'nextjs': { title: 'Next.js', backend: false, icon: NextJsLogoIcon },
  'remix': { title: 'Remix', backend: false, icon: RemixLogoIcon },
  'vanilla': { title: 'Vanilla', backend: true, icon: JavascriptLogoIcon },
  'react': { title: 'React', backend: true, icon: ReactLogoIcon },
  'client-only': { title: 'Client', backend: false, icon: () => null },
};

export const BACKEND_STACK = {
  nodejs: { title: 'Node.js', icon: NodeJsLogoIcon },
  ruby: { title: 'Ruby', icon: RubyLogoIcon },
  python: { title: 'Python', icon: PythonLogoIcon },
};

export type AvailableFrontend = keyof typeof FRONTEND_STACK;
export type AvailableBackend = keyof typeof BACKEND_STACK;

export const hasBackendStack = (frontend: AvailableFrontend | null) =>
  (frontend && FRONTEND_STACK[frontend].backend) || false;

export const getFrontendStack = <T extends AvailableFrontend | null>(
  queryFrontend: string | string[] | undefined,
  fallback: T,
): AvailableFrontend | T => {
  const frontend = queryFrontend?.toString();

  if (frontend && frontend in FRONTEND_STACK) {
    // Introducing lint rule banning type assertions
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return frontend as AvailableFrontend;
  }

  return fallback;
};

export const getBackendStack = <T extends AvailableBackend | null>(
  queryBackend: string | string[] | undefined,
  fallback: T,
): AvailableBackend | T => {
  const backend = queryBackend?.toString();

  if (backend && backend in BACKEND_STACK) {
    // Introducing lint rule banning type assertions
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return backend as AvailableBackend;
  }

  return fallback;
};

interface Slug {
  params: {
    slug: string[];
  };
}

/**
 * Returns an array of slugs for all the possible combinations of frontend and backend stacks.
 */
export const getStackSlugs = (baseSlugs: string[] = []) => {
  const slugs: Slug[] = [];

  for (const [frontendKey, frontendData] of Object.entries(FRONTEND_STACK)) {
    if (frontendData.backend) {
      for (const [backendKey] of Object.entries(BACKEND_STACK)) {
        slugs.push({
          params: { slug: [...baseSlugs, frontendKey, backendKey] },
        });
      }
    } else {
      slugs.push({
        params: { slug: [...baseSlugs, frontendKey] },
      });
    }
  }

  return slugs;
};
